/**
 * Validators barrel for @cloudnest/cascadia-engine.
 *
 * Note: The `Init` action has no standalone validator — its validation logic
 * lives inside `initGame()` in `src/init-game.ts`, which returns a
 * `Result<GameState, string>`. There is nothing to validate against an existing
 * GameState for Init (Init creates the state from scratch).
 *
 * All other 12 action variants have a dedicated `<action>.validator.ts` file.
 *
 * `dispatchValidator` is the top-level discriminator that routes an `Action`
 * to the correct validator function.
 */

import type { Action } from "../types/actions";
import type { ValidationResult } from "../types/common";
import type { GameState } from "../types/game-state";
import { validateChooseLandmarkCard } from "./choose-landmark-card.validator";
import { validateDraftDecoupled } from "./draft-decoupled.validator";
import { validateDraftPair } from "./draft-pair.validator";
import { validateEndTurn } from "./end-turn.validator";
import { validatePlaceTile } from "./place-tile.validator";
import { validatePlaceWildlife } from "./place-wildlife.validator";
import { validateReturnWildlife } from "./return-wildlife.validator";
import { validateSkipLandmark } from "./skip-landmark.validator";
import { validateSpendNatureDecouple } from "./spend-nature-decouple.validator";
import { validateSpendNatureWipe } from "./spend-nature-wipe.validator";
import { validateUnlockLandmark } from "./unlock-landmark.validator";
import { validateWipeMarketOptional } from "./wipe-market-optional.validator";

export { validateChooseLandmarkCard } from "./choose-landmark-card.validator";
export { checkPhase, checkVersion, commonGuards, VALID } from "./common-guards";
export { validateDraftDecoupled } from "./draft-decoupled.validator";
export { validateDraftPair } from "./draft-pair.validator";
export { validateEndTurn } from "./end-turn.validator";
export { validatePlaceTile } from "./place-tile.validator";
export { validatePlaceWildlife } from "./place-wildlife.validator";
export { validateReturnWildlife } from "./return-wildlife.validator";
export { validateSkipLandmark } from "./skip-landmark.validator";
export { validateSpendNatureDecouple } from "./spend-nature-decouple.validator";
export { validateSpendNatureWipe } from "./spend-nature-wipe.validator";
export { validateUnlockLandmark } from "./unlock-landmark.validator";
export { validateWipeMarketOptional } from "./wipe-market-optional.validator";

/**
 * Top-level discriminator. Routes `action` to the correct validator.
 * Returns `ERR_UNKNOWN_ACTION` for the `Init` action type (Init is
 * handled by `initGame`, not by this dispatcher).
 */
export const dispatchValidator = (
  state: GameState,
  action: Action,
): ValidationResult => {
  switch (action.type) {
    case "Init":
      // Init has no runtime validator — handled by initGame().
      return {
        valid: false,
        error: "ERR_UNKNOWN_ACTION",
        details: { type: action.type },
      };
    case "DraftPair":
      return validateDraftPair(state, action);
    case "DraftDecoupled":
      return validateDraftDecoupled(state, action);
    case "SpendNatureDecouple":
      return validateSpendNatureDecouple(state, action);
    case "SpendNatureWipe":
      return validateSpendNatureWipe(state, action);
    case "WipeMarketOptional":
      return validateWipeMarketOptional(state, action);
    case "PlaceTile":
      return validatePlaceTile(state, action);
    case "PlaceWildlife":
      return validatePlaceWildlife(state, action);
    case "ReturnWildlife":
      return validateReturnWildlife(state, action);
    case "UnlockLandmark":
      return validateUnlockLandmark(state, action);
    case "ChooseLandmarkCard":
      return validateChooseLandmarkCard(state, action);
    case "SkipLandmark":
      return validateSkipLandmark(state, action);
    case "EndTurn":
      return validateEndTurn(state, action);
  }
};
